import React, { useState } from 'react';
import {
  Globe, Palette, Shield, Database, Key,
  Users, Save, Check,
  Mail, Eye, EyeOff, Brush, Server, Cpu,
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import ToggleSwitch from '../../components/ui/ToggleSwitch';

/* ─── Reusable components ────────────────────────────────────────────── */

const Section = ({ icon: Icon, color = 'violet', title, description, children }) => {
  const colors = {
    violet: 'bg-violet-50 text-violet-600 border-violet-100',
    blue:   'bg-blue-50   text-blue-600   border-blue-100',
    green:  'bg-green-50  text-green-600  border-green-100',
    red:    'bg-red-50    text-red-600    border-red-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
    gray:   'bg-gray-50   text-gray-600   border-gray-100',
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-50 bg-gray-50/60 flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${colors[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="px-6 py-5 space-y-5">{children}</div>
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <label className="block text-xs font-semibold text-gray-700">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-400">{hint}</p>}
  </div>
);

const TextInput = ({ value, onChange, placeholder, type = 'text', ...rest }) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400 transition-colors placeholder:text-gray-300"
    {...rest}
  />
);

const ToggleRow = ({ label, description, enabled, onToggle }) => (
  <div className="flex items-center justify-between gap-4 py-2">
    <div>
      <p className="text-sm font-medium text-gray-800">{label}</p>
      {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
    </div>
    <ToggleSwitch enabled={enabled} onToggle={onToggle} />
  </div>
);

const SaveButton = ({ onClick, saved }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
      saved
        ? 'bg-green-50 text-green-700 border border-green-200'
        : 'bg-violet-600 hover:bg-violet-700 text-white'
    }`}
  >
    {saved ? <><Check className="w-4 h-4" /> Saved</> : <><Save className="w-4 h-4" /> Save Changes</>}
  </button>
);

const Divider = () => <div className="h-px bg-gray-100" />;

/* ─── Main component ─────────────────────────────────────────────────── */

const AdminSettings = () => {
  // Platform
  const [platformName, setPlatformName] = useState('iVisora');
  const [siteUrl, setSiteUrl]           = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [platformSaved, setPlatformSaved] = useState(false);

  // Branding
  const [primaryColor, setPrimaryColor] = useState('#7c3aed');
  const [logoUrl, setLogoUrl]           = useState('');
  const [brandSaved, setBrandSaved]     = useState(false);

  // Email Notifications
  const [notifSubmission, setNotifSubmission]   = useState(true);
  const [notifNewUser, setNotifNewUser]         = useState(true);
  const [notifWeeklyReport, setNotifWeeklyReport] = useState(false);
  const [emailSaved, setEmailSaved]             = useState(false);

  // Security
  const [mfaRequired, setMfaRequired]         = useState(false);
  const [sessionTimeout, setSessionTimeout]   = useState('7');
  const [passwordPolicy, setPasswordPolicy]   = useState('medium');
  const [securitySaved, setSecuritySaved]     = useState(false);

  // Storage
  const [maxFileSize, setMaxFileSize]     = useState('10');
  const [storageQuota, setStorageQuota]   = useState('500');
  const [storageSaved, setStorageSaved]   = useState(false);

  // API Keys
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [apiKey]                          = useState('sk-ivisora-xxxxxxxxxxxxxxxxxxxxxxxx');

  // Designer Access
  const [designerRegistration, setDesignerRegistration]   = useState(true);
  const [designerAutoApprove, setDesignerAutoApprove]     = useState(false);
  const [designerMaxTemplates, setDesignerMaxTemplates]   = useState('50');
  const [designerRevision, setDesignerRevision]           = useState(true);
  const [accessSaved, setAccessSaved]                     = useState(false);

  // System
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [debugMode, setDebugMode]             = useState(false);
  const [systemSaved, setSystemSaved]         = useState(false);

  const save = (setter) => {
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-sm text-gray-400 mt-0.5">Configure your iVisora platform.</p>
        </div>

        {/* 2-column grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">

          {/* Platform */}
          <Section icon={Globe} color="violet" title="Platform" description="General platform configuration">
            <Field label="Platform Name">
              <TextInput value={platformName} onChange={e => setPlatformName(e.target.value)} placeholder="iVisora" />
            </Field>
            <Field label="Site URL" hint="The canonical URL for your platform.">
              <TextInput value={siteUrl} onChange={e => setSiteUrl(e.target.value)} placeholder="https://ivisora.com" />
            </Field>
            <Field label="Support Email">
              <TextInput value={supportEmail} onChange={e => setSupportEmail(e.target.value)} placeholder="support@ivisora.com" type="email" />
            </Field>
            <SaveButton onClick={() => save(setPlatformSaved)} saved={platformSaved} />
          </Section>

          {/* Branding */}
          <Section icon={Brush} color="orange" title="Branding" description="Logo, colors and visual identity">
            <Field label="Logo URL" hint="Hosted image URL for your platform logo.">
              <TextInput value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="https://cdn.example.com/logo.png" />
            </Field>
            <Field label="Primary Accent Color">
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={e => setPrimaryColor(e.target.value)}
                  className="w-10 h-9 rounded-lg border border-gray-200 cursor-pointer p-0.5 bg-white"
                />
                <TextInput value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} placeholder="#7c3aed" />
              </div>
            </Field>
            <div className="rounded-xl border-2 border-dashed border-gray-100 p-4 text-center">
              <Palette className="w-6 h-6 text-gray-200 mx-auto mb-1" />
              <p className="text-xs text-gray-300">Font & advanced theming coming soon</p>
            </div>
            <SaveButton onClick={() => save(setBrandSaved)} saved={brandSaved} />
          </Section>

          {/* Email Notifications */}
          <Section icon={Mail} color="blue" title="Email Notifications" description="When to send platform emails">
            <div className="divide-y divide-gray-50">
              <ToggleRow
                label="New Submission Alert"
                description="Email when a designer submits a template."
                enabled={notifSubmission}
                onToggle={() => setNotifSubmission(v => !v)}
              />
              <ToggleRow
                label="New User Signup"
                description="Email when a new user registers."
                enabled={notifNewUser}
                onToggle={() => setNotifNewUser(v => !v)}
              />
              <ToggleRow
                label="Weekly Summary Report"
                description="Weekly digest of platform activity."
                enabled={notifWeeklyReport}
                onToggle={() => setNotifWeeklyReport(v => !v)}
              />
            </div>
            <SaveButton onClick={() => save(setEmailSaved)} saved={emailSaved} />
          </Section>

          {/* Security */}
          <Section icon={Shield} color="red" title="Security" description="Access control and session policies">
            <div className="divide-y divide-gray-50">
              <ToggleRow
                label="Require MFA for Admins"
                description="All admin accounts must use two-factor auth."
                enabled={mfaRequired}
                onToggle={() => setMfaRequired(v => !v)}
              />
            </div>
            <Divider />
            <Field label="Session Timeout (days)" hint="How long before users are logged out.">
              <TextInput value={sessionTimeout} onChange={e => setSessionTimeout(e.target.value)} placeholder="7" type="number" />
            </Field>
            <Field label="Password Policy">
              <select
                value={passwordPolicy}
                onChange={e => setPasswordPolicy(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400"
              >
                <option value="low">Low — 6+ characters</option>
                <option value="medium">Medium — 8+ chars, mixed case</option>
                <option value="high">High — 12+ chars, symbols required</option>
              </select>
            </Field>
            <SaveButton onClick={() => save(setSecuritySaved)} saved={securitySaved} />
          </Section>

          {/* Storage */}
          <Section icon={Database} color="green" title="Storage" description="File upload limits and quotas">
            <Field label="Max File Upload Size (MB)" hint="Per-file limit for template assets.">
              <TextInput value={maxFileSize} onChange={e => setMaxFileSize(e.target.value)} placeholder="10" type="number" />
            </Field>
            <Field label="Designer Storage Quota (MB)" hint="Total storage per designer account.">
              <TextInput value={storageQuota} onChange={e => setStorageQuota(e.target.value)} placeholder="500" type="number" />
            </Field>
            <div className="rounded-xl border-2 border-dashed border-gray-100 p-4 text-center">
              <Server className="w-6 h-6 text-gray-200 mx-auto mb-1" />
              <p className="text-xs text-gray-300">S3 / CDN integration coming soon</p>
            </div>
            <SaveButton onClick={() => save(setStorageSaved)} saved={storageSaved} />
          </Section>

          {/* API Keys */}
          <Section icon={Key} color="gray" title="API Keys" description="Manage developer access tokens">
            <Field label="Platform API Key" hint="Use this key to access the iVisora public API.">
              <div className="flex items-center gap-2">
                <TextInput
                  value={apiKeyVisible ? apiKey : '••••••••••••••••••••••••••••••••'}
                  readOnly
                  className="font-mono text-xs"
                />
                <button
                  onClick={() => setApiKeyVisible(v => !v)}
                  className="p-2 text-gray-400 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {apiKeyVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </Field>
            <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
              <p className="text-xs text-amber-700 font-medium">Keep your API key secret. Do not commit it to source control.</p>
            </div>
            <button className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-gray-200 hover:bg-gray-50 text-gray-700 transition-colors">
              <Key className="w-4 h-4" />
              Regenerate Key
            </button>
          </Section>
        </div>

        {/* Full-width: Designer Access */}
        <Section icon={Users} color="violet" title="Designer Access" description="Control how designers register and submit templates">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Registration</p>
              <div className="divide-y divide-gray-50">
                <ToggleRow
                  label="Open Registration"
                  description="Allow anyone to sign up as a designer."
                  enabled={designerRegistration}
                  onToggle={() => setDesignerRegistration(v => !v)}
                />
                <ToggleRow
                  label="Auto-Approve Accounts"
                  description="Skip manual review for new designer accounts."
                  enabled={designerAutoApprove}
                  onToggle={() => setDesignerAutoApprove(v => !v)}
                />
              </div>
            </div>
            <div className="space-y-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Submission Limits</p>
              <Field label="Max Templates per Designer">
                <TextInput value={designerMaxTemplates} onChange={e => setDesignerMaxTemplates(e.target.value)} placeholder="50" type="number" />
              </Field>
            </div>
            <div className="space-y-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Review Flow</p>
              <div className="divide-y divide-gray-50">
                <ToggleRow
                  label="Enable Revision Requests"
                  description="Admins can request changes before approving."
                  enabled={designerRevision}
                  onToggle={() => setDesignerRevision(v => !v)}
                />
              </div>
            </div>
          </div>
          <Divider />
          <SaveButton onClick={() => save(setAccessSaved)} saved={accessSaved} />
        </Section>

        {/* Full-width: System */}
        <Section icon={Cpu} color="gray" title="System" description="Maintenance and debug options">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="divide-y divide-gray-50">
              <ToggleRow
                label="Maintenance Mode"
                description="Show maintenance page to all non-admin visitors."
                enabled={maintenanceMode}
                onToggle={() => setMaintenanceMode(v => !v)}
              />
            </div>
            <div className="divide-y divide-gray-50">
              <ToggleRow
                label="Debug Logging"
                description="Write verbose logs to the server console."
                enabled={debugMode}
                onToggle={() => setDebugMode(v => !v)}
              />
            </div>
          </div>
          <Divider />
          <SaveButton onClick={() => save(setSystemSaved)} saved={systemSaved} />
        </Section>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
